import { useCallback, useEffect } from 'react';
import { useProperty } from '@vibesites/runtime/react';

/** Same IDs as the `cort-lead-form` block so the two never load the script twice. */
const SCRIPT_ID = 'CortFormLoader';
const FORM_DIV_ID = 'CortFormDiv';
const SCRIPT_URL = 'https://api-assets.cort.com/scripts/CortFormLoader.js';

type CortWindow = Window & {
  loadCortForm?: (formId: string, mode: string) => void;
  Cort_modal?: { open?: () => void };
};

let pendingOpen = false;

function openNow(): boolean {
  const modal = (window as CortWindow).Cort_modal;
  if (modal && typeof modal.open === 'function') {
    modal.open();
    return true;
  }
  return false;
}

/** Reads `custom_cort_lead_form_id` from the property. */
export function useCortLeadFormId(): string | null {
  const property = useProperty() as Record<string, unknown> | null;
  const raw = property?.custom_cort_lead_form_id;
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  return trimmed || null;
}

/**
 * Loads CortFormLoader.js in Custom mode for the property's CORT form ID and
 * returns an opener for the CORT modal. Returns `null` when no form ID is set.
 */
export function useCortLeadForm(enabled = true): (() => void) | null {
  const formId = useCortLeadFormId();
  const active = enabled && !!formId;

  useEffect(() => {
    if (!active || !formId || typeof document === 'undefined') return;
    if (document.getElementById(SCRIPT_ID)) return;

    let createdDiv: HTMLDivElement | null = null;
    if (!document.getElementById(FORM_DIV_ID)) {
      createdDiv = document.createElement('div');
      createdDiv.id = FORM_DIV_ID;
      createdDiv.hidden = true;
      createdDiv.setAttribute('aria-hidden', 'true');
      document.body.appendChild(createdDiv);
    }

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = SCRIPT_URL;
    script.async = true;
    script.onload = () => {
      const w = window as CortWindow;
      if (typeof w.loadCortForm === 'function') w.loadCortForm(formId, 'Custom');
      if (pendingOpen) {
        let tries = 0;
        const timer = window.setInterval(() => {
          if (openNow() || ++tries > 40) {
            pendingOpen = false;
            window.clearInterval(timer);
          }
        }, 100);
      }
    };
    document.body.appendChild(script);

    return () => {
      script.remove();
      createdDiv?.remove();
    };
  }, [active, formId]);

  const open = useCallback(() => {
    if (!openNow()) pendingOpen = true;
  }, []);

  return active ? open : null;
}

/** Match amenity titles that should open the CORT furnished-apartments form. */
export function isFurnishedAmenityTitle(title: string | null | undefined): boolean {
  if (!title) return false;
  return title.trim().toLowerCase().startsWith('furnished');
}
